import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export default function FAQ() {
  const [expanded, setExpanded] = React.useState([]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(
      isExpanded ? [...expanded, panel] : expanded.filter((item) => item !== panel),
    );
  };

  return (
    <Container
      id="faq"
      sx={{
        pt: { xs: 4, sm: 8 },
        pb: { xs: 4, sm: 8 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 },
      }}
    >
      {/* Border line with gradient */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100vw',
          height: '1px',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />
      {/* Background gradient */}
      <Box
        sx={(theme) => ({
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100vw',
          height: '400px',
          pointerEvents: 'none',
          zIndex: 0,
        })}
      />
      <Box
        sx={{
          width: { sm: '100%', md: '60%' },
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            color: '#797979',
            fontSize: '1rem',
            mb: 1,
          }}
        >
          FAQ
        </Typography>
        <Typography
          component="h2"
          sx={{
            color: '#FFF',
            fontWeight: 500,
            fontSize: { xs: '2rem', sm: '2.75rem' },
            lineHeight: 1.15,
          }}
        >
          Frequently Asked
          <br />
          Questions
        </Typography>
      </Box>
      <Box sx={{ width: '100%' }}>
        <Accordion
          expanded={expanded.includes('panel1')}
          onChange={handleChange('panel1')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Typography component="span" variant="subtitle2">
              How do I contact customer support if I have a question or issue?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              For immediate assistance, <b>use the live chat button located at the bottom right of the app</b>. For more in-depth support or if you're not yet a customer, please email us at&nbsp;
              <Link href="mailto:support@tradespad.com">support@tradespad.com</Link>.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('panel2')}
          onChange={handleChange('panel2')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2d-content"
            id="panel2d-header"
          >
            <Typography component="span" variant="subtitle2">
              What if it doesn&apos;t meet my expectations?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              We're confident you'll love Tradespad! That's why we offer a <b>free 7-day trial</b>. If you're not satisfied, you can cancel your subscription before the trial ends and you won't be charged.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('panel3')}
          onChange={handleChange('panel3')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Typography component="span" variant="subtitle2">
              What platforms does TradesPad support?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              You can connect TradesPad for trade journaling with the following platforms:
              <ul>
                <li>MetaTrader 4 (Soon)</li>
                <li>MetaTrader 5</li>
                <li>cTrader</li>
                <li>TradeLocker</li>
                <li>Matchtrader (Soon)</li>
                <li>Ninjatrader</li>
                <li>Tradovate</li>
              </ul>

              If your platform isn't listed, don't worry! You can still manually enter your trades and enjoy TradesPad's other features.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('panel4')}
          onChange={handleChange('panel4')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Typography component="span" variant="subtitle2">
              Why should I use TradesPad?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              It's well known among traders that keeping track of your accounts and trades is essential for consistent results.
              At TradesPad, you get that and more – everything you need as a trader in a single application.
              <ul>
                <li>Automatic integration with your accounts (CFDs, Futures, Options, etc.)</li>
                <li>Minimalist and visually appealing trade journal</li>
                <li>Real-time, high-impact news integrated into the calendar</li>
                <li>Tracking and control of your different strategies, with performance analysis for each</li>
                <li>Automatic analysis of your most important statistics as a trader</li>
                <li>Track record (Coming soon)</li>
                <li>Backtesting of your strategies (Coming Soon)</li>
                <li>Mobile application to take your trading anywhere (Coming Soon)</li>
              </ul>
              All this and much more for a very reasonable price, and with an affiliate plan where you can generate income while using the best trading journal on the market.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded.includes('panel5')}
          onChange={handleChange('panel5')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Typography component="span" variant="subtitle2">
              Can I use TradesPad with my funded trading accounts?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              variant="body2"
              gutterBottom
              sx={{ maxWidth: { sm: '100%', md: '70%' } }}
            >
              Absolutely! You can and <b>should</b> use Tradespad to meticulously track and analyze your funded trading accounts, whether they're in the evaluation phase or active payout accounts.

              Tradespad doesn't use external IP addresses when connecting to your accounts, ensuring you remain fully compliant with the IP address policies of all prop firms.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Container>
  );
}